import React, { useEffect, useState } from "react"
import _isEmpty from "lodash/isEmpty"
import _get from "lodash/get"
import AemGrid from "@/components/AemGrid"
import {
  CORE_GLOBAL_CONFIG_URL,
  footerUrl,
  headerUrl,
  previewContentPrefix,
  groutBostikChartUrl,
  publicWebsiteUrl,
  EMPTY_PIXEL,
} from "@/constants"
import { aemAxiosInstance, aemPreviewAxiosInstance } from "@/constants/api"
import { getConfig } from "@/constants/config"
import { useDispatch, useSelector } from "react-redux"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n } from "next-i18next"
import useIsSsr from "@/hooks/useIsSsr"
import { useRouter } from "next/router"
import usePageAuth from "@/hooks/usePageAuth"
import {
  filterBackEndJson,
  generateCanonicalUrl,
  getClienIP,
  getDataLayerObj,
  getUserPersona,
  removeAndCleanLink,
  updateAnalyticPageUrl,
  lowercaseKeys,
  isLoadTargetAnalyticsEventLoaded,
} from "@/utils/helper"
import { getTeaserFragments } from "@/components/ProductDetail/v3/productDetailsHelper"
import { getProductDetailsFromServer } from "@/utils/product"

import {
  selectGenericState,
  setGbhDataLayerCustom,
} from "@/store/features/genericSlice"
import { selectAuthState } from "@/store/features/authSlice"
import { selectPdpState } from "@/store/features/pdpSlice"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import GlobalPageLayout from "@/components/Shared/GlobalPage/GlobalPageLayout"
import ProductDetail from "@/components/ProductDetail/v1/ProductDetail.jsx"
import ProductTabDetail from "@/components/ProductDetail/v1/ProductTabDetail"
import PairsWellWith from "@/components/PairsWellWith/PairsWellWith"
import ProductAccordion from "@/components/ProductDetail/v3/ProductAccordion"
import Review from "@/components/Review/v1/Review"
import ProductExf from "@/components/ProductDetail/v3/ProductLandingTeasers/ProductExf"
import PDPLoader from "@/components/ProductDetail/v1/PDPLoader"
import NextHead from "@/components/core/Head/NextHead"
import PDPCuratedProducts from "@/components/ProductDetail/v3/ProductDetailView/PDPCuratedProducts"
import Script from "next/script"
/**
 * renders ProductDetailsPage page
 * @return {JSX.Element}
 */
function ProductDetailsPage({
  tmpData: data = {},
  headertmp: header,
  footertmp: footer,
  ip,
  globalScript,
  footerScript,
  analyticsData,
  gbhDataLayerCustom,
  slug,
  tmpSkuId: skuId,
  type,
  pdpData,
  apiStatus: status,
  apiStatusText: statusText,
  initialImgAlt,
  initialImgUrl,
  initialImageFullUrl,
  fullUrl,
  isPart,
  siteName,
  metaContent,
  groutData,
}) {
  const dispatch = useDispatch()
  const router = useRouter()
  const isSsr = useIsSsr()
  const { access_token: accessToken } = useSelector(selectAuthState)
  const { pageIsInteractive } = useSelector(selectGenericState)
  const { isTeaserLoaded } = useSelector(selectPdpState)
  const [pageRedirect, setPageRedirect] = useState("")
  const { redirectTarget: { page: { path = "" } = {} } = {} } = data
  const [config, setConfig] = useState({})
  const [priceSpiderKey, setPriceSpiderKey] = useState("")
  const [priceSpiderLanguage, setPriceSpiderLanguage] = useState("")
  const [priceSpiderCountry, setPriceSpiderCountry] = useState("")
  const [isBundleProduct, setIsBundleProduct] = useState("")
  const [currencyCode, setCurrencyCode] = useState("")
  const [persona, setPersona] = useState("")
  const pdpRouteKey = `${slug}:${skuId || ""}`

  useEffect(() => {
    getConfig().then(config => setConfig(config))
    if (!isLoadTargetAnalyticsEventLoaded()) {
      window.initDataLayerTop()
    }
    setPersona(getUserPersona())
  }, [])

  useEffect(() => {
    scrollToTop()
    const {
      ProductLocalCategory_s: productLocalCategory = "",
      Product_Category: productCategory = "",
      CustomerFacingSKU_s: skuID = "",
      CustomerFacingBrand_s: siteName = "",
    } = pdpData || {}
    window.needPdpdata = {
      productSubCatergory:
        productLocalCategory?.toLowerCase() || productCategory?.toLowerCase(),
      skuID,
      siteName: siteName.toLowerCase(),
    }
  }, [pdpData, slug, skuId])

  useEffect(() => {
    if (gbhDataLayerCustom) {
      dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    }
    return () => dispatch(setGbhDataLayerCustom(""))
  }, [dispatch, gbhDataLayerCustom])

  useEffect(() => {
    if (!_isEmpty(config)) {
      const psKey = _get(config, "general.priceSpiderKey", "")
      if (psKey) {
        setPriceSpiderKey(psKey)
      }
      const psLanguage = _get(config, "internationalization.language", "")
      if (psLanguage) {
        setPriceSpiderLanguage(psLanguage)
      }
      const psCountry = _get(config, "internationalization.country", "")
      if (psCountry) {
        setPriceSpiderCountry(psCountry)
      }
      const currencyCode = _get(config, "internationalization.currencyCode", "")
      if (currencyCode) {
        setCurrencyCode(currencyCode)
      }
    }
  }, [config])

  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])

  usePageAuth()

  useEffect(() => {
    if (analyticsData) getDataLayerObj(analyticsData)
  }, [analyticsData])

  useEffect(() => {
    if (path) {
      setPageRedirect(removeAndCleanLink(path))
    }
  }, [path])

  useEffect(() => {
    if (!isSsr && pageRedirect) {
      router.push(pageRedirect)
    }
  }, [isSsr, pageRedirect])

  useEffect(() => {
    if (pdpData && accessToken && pageIsInteractive && !isTeaserLoaded) {
      getTeaserFragments(data, pdpData)
    }
  }, [accessToken, pdpData, pageIsInteractive, isTeaserLoaded])

  const scrollToTop = () => {
    window.scrollTo(0, 0)
  }

  const { language: metaLanguage = "", accountName = "" } = data

  return (
    <>
      <NextHead
        psKey={priceSpiderKey}
        psCountry={priceSpiderCountry}
        psLanguage={priceSpiderLanguage}
        fullUrl={fullUrl}
        metaLanguage={metaLanguage}
        pdpData={pdpData}
        isPart={isPart}
        initialImageFullUrl={initialImageFullUrl}
        skuId={skuId}
        siteName={siteName}
        isBundleProduct={isBundleProduct}
        currencyCode={currencyCode}
        persona={persona}
      />
      <Script
        key="zoomdatapdp"
        id="zoomdatapdp"
        src="https://s7d1.scene7.com/s7viewers/html5/js/ZoomViewer.js"
      />

      <GlobalPageLayout
        header={
          header ? (
            <GlobalPageHeader
              header={header}
              title={pdpData?.metaTitle_s}
              description={pdpData?.metaDescription_s}
              templateName={data?.templateName}
              globalScripting={globalScript}
            />
          ) : null
        }
        footer={
          footer ? (
            <GlobalPageFooter footer={footer} footerScripting={footerScript} />
          ) : null
        }
      >
        {data && !_isEmpty(pdpData) ? (
          <div className="min-vh-100" key={pdpRouteKey}>
            <AemGrid
              key={pdpRouteKey}
              data={data}
              count={0}
              productDetail={(data, slug, skuId, type) => (
                <ProductDetail
                  data={{ ...data, accountName }}
                  pdpData={pdpData}
                  groutData={groutData}
                  slug={slug}
                  skuId={skuId}
                  type={type}
                  initialImgAlt={initialImgAlt}
                  initialImgUrl={initialImgUrl}
                  setIsBundleProduct={setIsBundleProduct}
                  config={config}
                />
              )}
              productTabDetails={data => <ProductTabDetail authorData={data} />}
              productDetailFiller={data => (
                <PDPLoader
                  initialImgAlt={initialImgAlt}
                  initialImgUrl={initialImgUrl}
                  pdpJson={data}
                  pdpData={pdpData}
                  skuId={skuId}
                />
              )}
              pairsWellWith={data => <PairsWellWith data={data} skuId={skuId} />}
              productAccordion={data => <ProductAccordion data={data} />}
              review={data => <Review data={data} />}
              productExf={data => <ProductExf data={data} />}
              slug={slug}
              skuId={skuId}
              type={type}
              pdpCuratedProduct={data => (
                <PDPCuratedProducts authData={data} slug={slug} skuId={skuId} />
              )}
            />
          </div>
        ) : null}
      </GlobalPageLayout>
    </>
  )
}

/**
 *
 * @return {Object}
 */
export async function getServerSideProps({
  req,
  res,
  locale,
  query,
  resolvedUrl,
  params,
}) {
  const { url = "" } = req
  const { leafcategory: slug = "", skuId = "", products = "" } = query
  const isPart = products === ""
  let urlPathWithoutParams = url?.split("?")[0] ?? `/${locale}`
  if (req.url.indexOf(".json") > -1 && req.url.indexOf("_next") > -1) {
    let allVariables = decodeURIComponent(resolvedUrl).split("?")[1].split("&")
    let newPath = "/p/{products}/{leafcategory}"
    allVariables.forEach(item => {
      const keyVal = item.split("=")
      switch (keyVal[0]) {
        case "products":
          newPath = newPath.replace("{products}", keyVal[1])
          break
        case "leafcategory":
          newPath = newPath.replace("{leafcategory}", keyVal[1])
          break
        default:
          break
      }
    })

    urlPathWithoutParams = newPath
  }
  let response = null
  try {
    response = await aemAxiosInstance({
      url: `${CORE_GLOBAL_CONFIG_URL}?caConfig=true&path=/${urlPathWithoutParams}`,
      method: "GET",
    })
  } catch (error) {
    return {
      notFound: true,
    }
  }
  const { data: configData = {} } = response
  let profileName = isPart ? `profile_service_parts_PDP` : `profile_kohler_PDP`
  let accountName = ""
  let productCategoryMap = {}

  let collections = isPart ? "service_parts" : "kohler"
  if (!_isEmpty(configData)) {
    const {
      general: {
        serviceParts = "service_parts",
        lwAppName = "",
        scene7AccountName = "",
      } = {},
      annsacksPDPConfig = {},
    } = configData
    profileName = isPart
      ? `profile_${serviceParts}_PDP`
      : `profile_${lwAppName.toLowerCase()}_PDP`
    collections = isPart ? serviceParts : lwAppName.toLowerCase()
    accountName = scene7AccountName
    productCategoryMap = lowercaseKeys(annsacksPDPConfig?.map || annsacksPDPConfig)
  }

  const productMap = productCategoryMap[products]?.split("/")?.pop()

  const PRODUCT_URL = `/p/${productMap}.model.json`

  let tmpSkuId = skuId
  await i18n?.reloadResources()
  const isPreviewMode = req.cookies["aem-author-preview-mode"]
  const ip = getClienIP(req) ?? ""

  const fullUrl = generateCanonicalUrl(url, locale, publicWebsiteUrl)

  let data
  let apiStatus = 200
  let apiStatusText = ""
  try {
    const response = isPreviewMode
      ? await aemPreviewAxiosInstance({
          url: previewContentPrefix + `/${PRODUCT_URL}`,
          method: "GET",
        })
      : await aemAxiosInstance({
          url: previewContentPrefix + `/${PRODUCT_URL}`,
          method: "GET",
        })

    data = response?.data
    // code to add redirect logic
    const { redirectTarget = {}, pageData = {}, redirectUrl } = data || {}

    if (
      (pageData?.redirect && pageData?.redirect !== "n/a") ||
      (redirectUrl && redirectUrl !== "n/a")
    ) {
      let link =
        (redirectUrl && redirectUrl !== "n/a")
          ? removeAndCleanLink(redirectUrl).trim() || "/"
          : removeAndCleanLink(pageData?.redirect).trim() || "/"
      return {
        redirect: {
          destination: link,
          permanent: false,
        },
      }
    } else if (redirectTarget?.page?.path) {
      return {
        redirect: {
          destination: removeAndCleanLink(redirectTarget.page.path),
          permanent: false,
        },
      }
    }
  } catch (error) {
    const { response: { status = 500, statusText = "" } = {} } = error
    if (status) apiStatus = status
    if (statusText) apiStatusText = statusText
  }

  const {
    globalScript = "",
    footerScript = "",
    pageComponentProperties = {},
    data: rawAnalyticsData = "",
    datalayerPageName = "",
    eventPageType = "",
    headerExpFrg = "",
    footerExpFrg = "",
    siteName = "",
    topHeadScript = null,
    analyticsScriptClass = "",
  } = data

  const analyticsData = updateAnalyticPageUrl(rawAnalyticsData, fullUrl)

  const { data: header } = isPreviewMode
    ? await aemPreviewAxiosInstance({
        url: `${headerExpFrg ? headerExpFrg : headerUrl}.model.json`,
        method: "GET",
      })
    : await aemAxiosInstance({
        url: `${headerExpFrg ? headerExpFrg : headerUrl}.model.json`,
        method: "GET",
      })

  const { data: footer } = isPreviewMode
    ? await aemPreviewAxiosInstance({
        url: `${footerExpFrg ? footerExpFrg : footerUrl}.model.json`,
        method: "GET",
      })
    : await aemAxiosInstance({
        url: `${footerExpFrg ? footerExpFrg : footerUrl}.model.json`,
        method: "GET",
      })

  const pdpData = await getProductDetailsFromServer(
    profileName,
    collections,
    slug,
    locale
  )

  let groutData = {}

  if (_isEmpty(pdpData) || !pdpData) {
    apiStatus = 404
    apiStatusText = "Sorry. We could not find this page."
    return {
      notFound: true,
    }
  } else {
    if (!skuId && !isPart) {
      tmpSkuId = pdpData.masterSKU_s
    }
    if (pdpData?.Product_Category?.toLowerCase() === "grout") {
      try {
        const response = await aemPreviewAxiosInstance({
          url: `${groutBostikChartUrl}.model.json`,
          method: "GET",
        })
        if (response?.status === 200) {
          groutData =
            response?.data[":items"]?.root[":items"]?.pdpgroutchart || {}
        }
      } catch (error) {
        console.error("Error fetching grout bostik chart data:", error)
      }
    }
  }
  let initialImgAlt = ""
  let initialImgUrl = ""
  let initialImageFullUrl = ""

  let tmpData = {}
  if (!_isEmpty(pageComponentProperties)) {
    tmpData = pageComponentProperties
  } else {
    tmpData = data
  }
  if (tmpSkuId) {
    let variants = []
    if (pdpData["variantList.sku_ss"]) {
      variants = pdpData["variantList.sku_ss"]
    }
    const variantImages = pdpData["productImages.labelWithUrl_ss"]
    let variantImageLabels = []
    let variantImageURLs = []
    if (variantImages?.length) {
      const selVarImageUrls = []
      const selVarImageLabels = []
      variantImages.forEach(img => {
        const imgDetail = img.split("|")
        if (tmpSkuId === imgDetail[0]) {
          selVarImageLabels.push(imgDetail[4] ?? "")
          selVarImageUrls.push(
            imgDetail[imgDetail.length - 1]
              ? imgDetail[imgDetail.length - 1]
              : EMPTY_PIXEL
          )
        } else if (!variants.find(v => v === imgDetail[0])) {
          variantImageLabels.push(imgDetail[4] ?? "")
          variantImageURLs.push(
            imgDetail[imgDetail.length - 1]
              ? imgDetail[imgDetail.length - 1]
              : EMPTY_PIXEL
          )
        }
      })
      variantImageLabels = [...selVarImageLabels, ...variantImageLabels]
      variantImageURLs = [...selVarImageUrls, ...variantImageURLs]
      if (variantImageURLs.length < 1) {
        variantImageLabels.push(tmpSkuId)
        variantImageURLs.push(EMPTY_PIXEL)
      }
    } else {
      variantImageLabels.push(tmpSkuId)
      variantImageURLs.push(EMPTY_PIXEL)
    }
    const assetIdArray = variantImageURLs
      ? variantImageURLs[0].split("PAWEB/")
      : [""]

    initialImgAlt = variantImageLabels[0]
    initialImgUrl = assetIdArray.length > 1 ? assetIdArray[1] : ""
    initialImageFullUrl =
      variantImageURLs.find(image => image.includes(initialImgUrl)) ?? ""
  }
  const gbhDataLayerCustom = {}
  if (datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = datalayerPageName
  }
  if (eventPageType) {
    gbhDataLayerCustom.eventPageType = eventPageType
  }
  if (data?.pageType) {
    gbhDataLayerCustom.pageType = data?.pageType
  }
  if (data) tmpData = filterBackEndJson(tmpData)
  let headertmp
  if (header) headertmp = filterBackEndJson(header)
  let footertmp
  if (footer) footertmp = filterBackEndJson(footer)
  const metaContent = data?.metaContent ?? ""
  return {
    props: {
      ip,
      tmpData: { ...tmpData, accountName },
      headertmp,
      footertmp,
      globalScript,
      footerScript,
      analyticsData,
      gbhDataLayerCustom,
      topHeadScript,
      analyticsScriptClass,
      slug,
      tmpSkuId,
      pdpData,
      metaContent,
      ...(await serverSideTranslations(locale, ["common"])),
      apiStatus,
      apiStatusText,
      initialImgAlt,
      initialImgUrl,
      initialImageFullUrl,
      fullUrl,
      isPart,
      siteName,
      urlPath: urlPathWithoutParams,
      groutData,
    },
  }
}

export default ProductDetailsPage
